// =============================================================================
// Consolidated XP Service
// Single source of truth for all XP, ranking, prediction, and monitoring operations.
// Replaces: rankingService, xpBettingService, xpCalculationService,
//   xpMonitorService, xpRecalculationService, xpSyncService, unifiedXPService
// =============================================================================

import {
  doc, getDoc, setDoc, updateDoc, addDoc, getDocs,
  collection, query, where, onSnapshot, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import userService from './userService';
import priceService from './priceService';
import { calculateXP } from '../utils/xp';
import logger from '../utils/logger';

class XPService {
  constructor() {
    // Rank definitions
    this.ranks = [
      { id: 'paperhands', name: 'Paper Hands', rank: 0, minXP: 0, maxXP: 500, color: '#9CA3AF', checkmark: null, canLoseXP: false, description: 'Safe zone - no XP loss here, learn the ropes' },
      { id: 'fartmark', name: 'Fartmark', rank: 1, minXP: 501, maxXP: 9999, color: '#8B4513', checkmark: '\u2713', canLoseXP: true, description: 'Brown checkmark - basic prediction skills' },
      { id: 'gold', name: 'Gold', rank: 2, minXP: 10000, maxXP: 49999, color: '#FFD700', checkmark: '\uD83E\uDD47', canLoseXP: true, description: 'Gold checkmark - skilled prediction maker' },
      { id: 'diamond', name: 'Diamond', rank: 3, minXP: 50000, maxXP: 199999, color: '#B9F2FF', checkmark: '\uD83D\uDC8E', canLoseXP: true, description: 'Diamond checkmark - elite prediction master' },
      { id: 'obsidian', name: 'Obsidian', rank: 4, minXP: 200000, maxXP: 999999999, color: '#1F2937', checkmark: '\uD83D\uDC51', canLoseXP: true, description: 'Obsidian checkmark - legendary prediction god' }
    ];

    // Market cap tiers for XP calculation
    this.marketCapTiers = [
      { min: 0, max: 10000, multiplier: 0.5, label: 'Micro (<$10K)' },
      { min: 10000, max: 100000, multiplier: 0.75, label: 'Small ($10K-$100K)' },
      { min: 100000, max: 500000, multiplier: 1.0, label: 'Medium ($100K-$500K)' },
      { min: 500000, max: 1000000, multiplier: 1.5, label: 'Large ($500K-$1M)' },
      { min: 1000000, max: 5000000, multiplier: 2.0, label: 'Mega ($1M-$5M)' },
      { min: 5000000, max: 10000000, multiplier: 3.0, label: 'Giant ($5M-$10M)' },
      { min: 10000000, max: 50000000, multiplier: 4.0, label: 'Titan ($10M-$50M)' },
      { min: 50000000, max: 100000000, multiplier: 5.0, label: 'Legendary ($50M-$100M)' },
      { min: 100000000, max: Infinity, multiplier: 6.0, label: 'Mythic ($100M+)' }
    ];

    // Target percentage tiers
    this.targetMultipliers = [
      { min: 0.05, max: 0.10, xpBase: 50, label: '5-10%' },
      { min: 0.10, max: 0.25, xpBase: 100, label: '10-25%' },
      { min: 0.25, max: 0.50, xpBase: 200, label: '25-50%' },
      { min: 0.50, max: 1.00, xpBase: 400, label: '50-100%' },
      { min: 1.00, max: 2.00, xpBase: 800, label: '2x-3x' },
      { min: 2.00, max: 4.00, xpBase: 1600, label: '3x-5x' },
      { min: 4.00, max: 9.00, xpBase: 3200, label: '5x-10x' },
      { min: 9.00, max: 49.00, xpBase: 6400, label: '10x-50x' },
      { min: 49.00, max: 99.00, xpBase: 12800, label: '50x-100x' },
      { min: 99.00, max: Infinity, xpBase: 25600, label: '100x+' }
    ];

    // Time multipliers
    this.timeMultipliers = [
      { hours: 1, multiplier: 0.5 },
      { hours: 6, multiplier: 0.75 },
      { hours: 24, multiplier: 1.0 },
      { hours: 72, multiplier: 1.25 },
      { hours: 168, multiplier: 1.5 },
      { hours: 336, multiplier: 1.75 },
      { hours: 720, multiplier: 2.0 }
    ];

    // Price call cache
    this.priceCallCache = new Map();
    this.checkInterval = null;
    this.expiredCheckInterval = null;

    // Real-time monitoring state (unified from xpMonitorService + xpSyncService)
    this.monitorListeners = new Map();
    this.monitorCallbacks = new Map();
    this.lastKnownXP = new Map();
    this.syncQueue = new Map();
    this.syncProcessing = false;

    // Trading post state
    this.pendingTrades = new Map();
    this.verificationQueue = [];
    this.processingInterval = null;

    // Start trade verification processing
    this._startTradeProcessing();
  }

  // ===========================================================================
  // CORE RANKING
  // ===========================================================================

  async getUserRanking(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'userRankings', userId));

      if (!userDoc.exists()) {
        const userProfileDoc = await getDoc(doc(db, 'users', userId));
        const userXP = userProfileDoc.exists() ? (userProfileDoc.data().xp || 0) : 0;

        const initialRanking = {
          userId,
          xp: userXP,
          currentRank: this.getRankByXP(userXP).id,
          totalCalls: 0,
          successfulCalls: 0,
          winRate: 0,
          activeCalls: [],
          xpHistory: [],
          createdAt: new Date(),
          updatedAt: new Date()
        };

        await setDoc(doc(db, 'userRankings', userId), initialRanking);
        return initialRanking;
      }

      const rankingData = userDoc.data();

      // Sync XP from users collection if higher (handles sync drift)
      const userProfileDoc = await getDoc(doc(db, 'users', userId));
      if (userProfileDoc.exists()) {
        const userXP = userProfileDoc.data().xp || 0;
        if (userXP > (rankingData.xp || 0)) {
          rankingData.xp = userXP;
          await updateDoc(doc(db, 'userRankings', userId), {
            xp: userXP,
            currentRank: this.getRankByXP(userXP).id
          });
        }
      }

      return rankingData;
    } catch (error) {
      logger.error('Failed to get user ranking:', error);
      throw error;
    }
  }

  getRankByXP(xp) {
    for (let i = this.ranks.length - 1; i >= 0; i--) {
      if (xp >= this.ranks[i].minXP) return this.ranks[i];
    }
    return this.ranks[0];
  }

  canUserLoseXP(userRanking) {
    return this.getRankByXP(userRanking.xp).canLoseXP;
  }

  // ===========================================================================
  // XP RISK & REWARD
  // ===========================================================================

  calculateXPRisk(userRanking, multiplier) {
    const rank = this.getRankByXP(userRanking.xp);

    const activeBonusXPAtRisk = (userRanking.activeCalls || []).reduce((sum, call) => {
      return sum + (call.bonusXPWager || 0);
    }, 0);

    const availableXP = Math.max(0, userRanking.xp - rank.minXP - activeBonusXPAtRisk);
    const rankMultiplier = Math.max(1, rank.rank * 0.3 + 1);

    let riskMultiplier = 1;
    if (multiplier <= 2) riskMultiplier = 1;
    else if (multiplier <= 5) riskMultiplier = 1.5;
    else if (multiplier <= 10) riskMultiplier = 2;
    else if (multiplier <= 25) riskMultiplier = 3;
    else riskMultiplier = 4;

    const calculatedRisk = Math.floor(15 * rankMultiplier * riskMultiplier);

    if (rank.id === 'paperhands') {
      return {
        canRisk: true, xpRisk: 0, availableXP, maxRisk: availableXP,
        reason: 'Paper Hands rank - protected from base XP loss but not bonus wagers'
      };
    }

    const maxRisk = Math.max(0, availableXP);
    const totalRisk = Math.min(calculatedRisk, maxRisk);

    if (maxRisk === 0) {
      return {
        canRisk: false, xpRisk: 0, availableXP: 0, maxRisk: 0,
        reason: 'No available XP above rank minimum to risk'
      };
    }

    return { canRisk: true, xpRisk: totalRisk, availableXP, maxRisk, reason: null };
  }

  calculatePotentialXPReward(xpRisk, multiplier, timeFrame) {
    const minBaseReward = 50;
    const baseReward = Math.max(minBaseReward, xpRisk * 2.5);
    const multiplierBonus = Math.pow(multiplier / 2, 1.5);

    const timeBonus = timeFrame <= 1 ? 4.0 :
                     timeFrame <= 3 ? 3.0 :
                     timeFrame <= 6 ? 2.5 :
                     timeFrame <= 12 ? 2.0 :
                     timeFrame <= 24 ? 1.5 : 1.0;

    return Math.max(25, Math.floor(baseReward * multiplierBonus * timeBonus));
  }

  // ===========================================================================
  // PREDICTION LIFECYCLE
  // ===========================================================================

  async getDailyPredictionCount(userId) {
    try {
      const userRanking = await this.getUserRanking(userId);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return (userRanking.activeCalls || []).filter(call => {
        const callDate = call.placedAt?.toDate ? call.placedAt.toDate() : new Date(call.placedAt);
        return callDate >= today;
      }).length;
    } catch (error) {
      logger.error('Failed to get daily prediction count:', error);
      return 0;
    }
  }

  async canUserMakePrediction(userId) {
    const userRanking = await this.getUserRanking(userId);
    const rank = this.getRankByXP(userRanking.xp);
    const dailyCount = await this.getDailyPredictionCount(userId);

    const tierLimits = { 'paperhands': 5, 'fartmark': 10, 'gold': 25, 'diamond': 100, 'obsidian': -1 };
    const limit = tierLimits[rank.id] || 5;

    if (limit === -1) return { canPredict: true, remaining: 'Unlimited', limit: 'Unlimited' };

    const remaining = limit - dailyCount;
    return { canPredict: remaining > 0, remaining: Math.max(0, remaining), limit, dailyCount };
  }

  async placePrediction(userId, call) {
    try {
      const [limitCheck, userRanking] = await Promise.all([
        this.canUserMakePrediction(userId),
        this.getUserRanking(userId)
      ]);

      if (!limitCheck.canPredict) {
        throw new Error(`Daily prediction limit reached! You've used all ${limitCheck.limit} predictions for today.`);
      }

      // Check for duplicate predictions (same token, multiplier, timeframe within 5 min)
      const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
      const duplicatePrediction = (userRanking.activeCalls || []).find(activeCall => {
        const callTime = activeCall.placedAt?.toDate ? activeCall.placedAt.toDate().getTime() : new Date(activeCall.placedAt).getTime();
        return activeCall.callData.tokenMint === call.tokenMint &&
               activeCall.callData.multiplier === call.multiplier &&
               activeCall.callData.timeFrame === call.timeFrame &&
               callTime > fiveMinutesAgo;
      });

      if (duplicatePrediction) {
        throw new Error(`Duplicate prediction detected! You already have an active prediction for ${call.tokenSymbol} at ${call.multiplier}x. Please wait or choose different parameters.`);
      }

      const riskCheck = this.calculateXPRisk(userRanking, call.multiplier);
      if (!riskCheck.canRisk) throw new Error(riskCheck.reason);

      const bonusXPWager = call.bonusXPWager || 0;
      const totalXPAtRisk = riskCheck.xpRisk + bonusXPWager;
      const baseReward = this.calculatePotentialXPReward(riskCheck.xpRisk, call.multiplier, call.timeFrame);
      const totalPotentialReward = call.totalPotentialReward || baseReward;

      const prediction = {
        id: `${userId}_${Date.now()}`,
        userId,
        callData: call,
        xpAtRisk: totalXPAtRisk,
        baseXPRisk: riskCheck.xpRisk,
        bonusXPWager,
        potentialReward: totalPotentialReward,
        status: 'active',
        placedAt: new Date(),
        expiresAt: new Date(call.expiresAt)
      };

      await updateDoc(doc(db, 'userRankings', userId), {
        activeCalls: [...(userRanking.activeCalls || []), prediction],
        totalCalls: (userRanking.totalCalls || 0) + 1,
        updatedAt: new Date()
      });

      return {
        success: true,
        prediction,
        newRanking: {
          ...userRanking,
          activeCalls: [...(userRanking.activeCalls || []), prediction],
          totalCalls: (userRanking.totalCalls || 0) + 1
        }
      };
    } catch (error) {
      logger.error('Failed to place prediction:', error);
      return { success: false, error: error.message };
    }
  }

  async completePrediction(userId, predictionId, won, customXpAwarded = null) {
    try {
      const userRanking = await this.getUserRanking(userId);
      const activeCall = userRanking.activeCalls?.find(call => call.id === predictionId);
      if (!activeCall) throw new Error('Active prediction not found');

      let xpChange = 0;
      const currentRank = this.getRankByXP(userRanking.xp);

      if (won) {
        xpChange = customXpAwarded !== null ? customXpAwarded : activeCall.potentialReward;
      } else {
        if (currentRank.canLoseXP) {
          xpChange = -activeCall.xpAtRisk;
        } else {
          // Paper Hands: protect base XP but consume bonus wager
          xpChange = -(activeCall.bonusXPWager || 0);
        }
      }

      const remainingCalls = userRanking.activeCalls.filter(call => call.id !== predictionId);
      const newXP = Math.max(0, userRanking.xp + xpChange);

      const updatedRanking = {
        ...userRanking,
        xp: newXP,
        activeCalls: remainingCalls,
        successfulCalls: won ? (userRanking.successfulCalls || 0) + 1 : (userRanking.successfulCalls || 0),
        winRate: ((userRanking.successfulCalls || 0) + (won ? 1 : 0)) / (userRanking.totalCalls || 1) * 100,
        xpHistory: [
          ...(userRanking.xpHistory || []),
          {
            change: xpChange,
            reason: won
              ? (customXpAwarded !== null && customXpAwarded < activeCall.potentialReward
                ? 'Prediction won (XP reduced due to risky token)'
                : 'Prediction won')
              : currentRank.canLoseXP
                ? 'Prediction lost'
                : activeCall.bonusXPWager > 0
                  ? 'Prediction lost (Bonus wager consumed)'
                  : 'Prediction lost (Paper Hands protection)',
            predictionId,
            timestamp: new Date()
          }
        ],
        updatedAt: new Date()
      };

      const newRank = this.getRankByXP(newXP);
      const rankChanged = currentRank.id !== newRank.id;
      updatedRanking.currentRank = newRank.id;

      await updateDoc(doc(db, 'userRankings', userId), updatedRanking);

      // Sync XP to users collection
      let userDoc;
      try {
        const userRef = doc(db, 'users', userId);
        userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          await updateDoc(userRef, { xp: newXP, lastXPUpdate: new Date() });
        }
      } catch (syncError) {
        logger.error('Error syncing XP to users collection:', syncError);
      }

      // Victory notification
      if (won) {
        try {
          await this.createNotification(userId, 'prediction_victory', {
            predictionId,
            postId: activeCall.postId,
            tokenSymbol: activeCall.tokenSymbol,
            tokenName: activeCall.tokenName,
            multiplier: activeCall.multiplier,
            xpEarned: xpChange,
            message: `Your ${activeCall.tokenSymbol} ${activeCall.multiplier}x prediction hit! +${xpChange} XP`
          });
        } catch (e) { /* notification failure is non-critical */ }
      }

      // Level up notification
      if (rankChanged && newRank.rank > currentRank.rank) {
        try {
          const userData = userDoc?.data();
          await this.createNotification(userId, 'levelup', {
            oldRank: currentRank.name,
            newRank: newRank.name,
            rankColor: newRank.color,
            checkmark: newRank.checkmark,
            username: userData?.username || 'User',
            message: `You leveled up to ${newRank.name}! ${newRank.checkmark || ''}`
          });
        } catch (e) { /* notification failure is non-critical */ }
      }

      return { success: true, xpChange, newRanking: updatedRanking };
    } catch (error) {
      logger.error('Failed to complete prediction:', error);
      return { success: false, error: error.message };
    }
  }

  // ===========================================================================
  // PRICE CALLS (Firestore CRUD)
  // ===========================================================================

  async submitPriceCall(userId, call) {
    try {
      // Check for duplicate active predictions
      const duplicateQuery = query(
        collection(db, 'priceCalls'),
        where('userId', '==', userId),
        where('status', '==', 'active')
      );

      const duplicateSnapshot = await getDocs(duplicateQuery);
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      let hasDuplicate = false;

      duplicateSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate().getTime() : new Date(data.createdAt).getTime();
        if (data.tokenMint === call.tokenMint && data.multiplier === call.multiplier &&
            data.timeFrame === call.timeFrame && createdAt > fiveMinutesAgo) {
          hasDuplicate = true;
        }
      });

      if (hasDuplicate) {
        throw new Error(`You already have an active prediction for ${call.tokenSymbol} at ${call.multiplier}x. Please wait a few minutes or choose different parameters.`);
      }

      const priceCallData = {
        userId,
        tokenMint: call.tokenMint,
        tokenSymbol: call.tokenSymbol,
        tokenName: call.tokenName,
        currentPrice: call.currentPrice,
        targetPrice: call.targetPrice,
        multiplier: call.multiplier,
        prediction: call.prediction,
        customMessage: call.customMessage || '',
        xpRisk: call.xpRisk || 0,
        bonusXPWager: call.bonusXPWager || 0,
        totalXPRisk: (call.xpRisk || 0) + (call.bonusXPWager || 0),
        potentialReward: call.potentialReward,
        timeFrame: call.timeFrame,
        expiresAt: call.expiresAt,
        riskLevel: call.riskLevel,
        tokenAmount: call.tokenAmount,
        currentValue: call.currentValue,
        targetValue: call.targetValue,
        marketCap: call.marketCap || 0,
        targetMarketCap: call.targetMarketCap || 0,
        xpReduction: call.xpReduction || 0,
        scamRiskLevel: call.scamRiskLevel || 'SAFE',
        status: 'active',
        createdAt: serverTimestamp(),
        isResolved: false,
        actualPrice: null,
        actualMultiplier: null,
        xpAwarded: 0
      };

      const docRef = await addDoc(collection(db, 'priceCalls'), priceCallData);
      this.priceCallCache.set(docRef.id, { ...priceCallData, id: docRef.id });

      return { success: true, callId: docRef.id };
    } catch (error) {
      logger.error('Error submitting price call:', error);
      return { success: false, error: error.message };
    }
  }

  async resolvePriceCall(callId, call, result, actualPrice = null, actualMultiplier = null) {
    try {
      // Prevent double-processing
      const callRef = doc(db, 'priceCalls', callId);
      const currentDoc = await getDoc(callRef);

      if (!currentDoc.exists()) return;

      const currentData = currentDoc.data();
      if (currentData.isResolved === true || currentData.status !== 'active') return;

      let xpAwarded = 0;
      let status = result;

      if (result === 'won') {
        const xpReduction = call.xpReduction || 0;
        const baseReward = call.potentialReward || 0;
        xpAwarded = Math.floor(baseReward * (1 - xpReduction / 100));
        await this.completePrediction(call.userId, callId, true, xpAwarded);
      } else if (result === 'expired') {
        status = 'lost';
        await this.completePrediction(call.userId, callId, false);
      }

      // Final atomic update
      await updateDoc(callRef, {
        status,
        isResolved: true,
        actualPrice,
        actualMultiplier,
        xpAwarded,
        resolvedAt: serverTimestamp()
      });

      // Update associated post
      if (call.postId) {
        try {
          await updateDoc(doc(db, 'posts', call.postId), {
            'predictionData.status': status,
            'predictionData.isResolved': true,
            'predictionData.actualPrice': actualPrice,
            'predictionData.actualMultiplier': actualMultiplier,
            'predictionData.xpAwarded': xpAwarded,
            'predictionData.resolvedAt': serverTimestamp()
          });
        } catch (e) { /* post update failure is non-critical */ }
      }

      this.priceCallCache.delete(callId);
    } catch (error) {
      logger.error(`Error resolving price call ${callId}:`, error);
    }
  }

  async getActivePriceCalls() {
    try {
      const { auth } = await import('../firebase');
      if (!auth.currentUser) return [];

      const q = query(
        collection(db, 'priceCalls'),
        where('status', '==', 'active'),
        where('isResolved', '==', false)
      );

      const querySnapshot = await getDocs(q);
      const calls = [];
      querySnapshot.forEach((docSnap) => {
        calls.push({ id: docSnap.id, ...docSnap.data() });
      });
      return calls;
    } catch (error) {
      if (error.code === 'permission-denied' || error.code === 'unauthenticated' ||
          error.message?.includes('Missing or insufficient permissions')) {
        return [];
      }
      logger.warn('Error fetching active price calls:', error.code || error.message);
      return [];
    }
  }

  // ===========================================================================
  // PRICE MONITORING (disabled - Cloud Functions handle this)
  // ===========================================================================

  startPriceMonitoring() {
    // Disabled: Firebase Cloud Functions handle price monitoring server-side
    return;
  }

  stopPriceMonitoring() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    if (this.expiredCheckInterval) {
      clearInterval(this.expiredCheckInterval);
      this.expiredCheckInterval = null;
    }
  }

  // ===========================================================================
  // REAL-TIME XP MONITORING & SYNC
  // Unified listener per user: subscribes to userRankings, syncs to users
  // collection, notifies callbacks, and queues post XP updates.
  // Replaces both xpMonitorService and xpSyncService.
  // ===========================================================================

  subscribeToUserXP(userId, onUpdate) {
    if (!userId) return () => {};

    // Add callback for this user
    const callbacks = this.monitorCallbacks.get(userId) || new Set();
    callbacks.add(onUpdate);
    this.monitorCallbacks.set(userId, callbacks);

    // If listener already exists for this user, just return unsubscribe
    if (this.monitorListeners.has(userId)) {
      return () => {
        const cbs = this.monitorCallbacks.get(userId);
        if (cbs) {
          cbs.delete(onUpdate);
          if (cbs.size === 0) this.stopMonitoring(userId);
        }
      };
    }

    // Create unified listener on userRankings document
    const userRankingRef = doc(db, 'userRankings', userId);

    const unsubscribe = onSnapshot(userRankingRef, async (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : null;
      const xp = data?.xp || 0;

      // Auto-sync XP to users collection if changed
      const lastXP = this.lastKnownXP.get(userId);
      if (lastXP !== undefined && lastXP !== xp) {
        try {
          const userRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists() && userDoc.data().xp !== xp) {
            await updateDoc(userRef, { xp, lastXPUpdate: new Date() });
          }
        } catch (e) { /* sync failure non-critical */ }

        // Queue post XP updates (debounced)
        this.syncQueue.set(userId, xp);
        if (!this.syncProcessing) {
          this.syncProcessing = true;
          setTimeout(() => this._processSync(), 500);
        }
      }
      this.lastKnownXP.set(userId, xp);

      // Notify all callbacks for this user
      const xpData = {
        xp,
        activeCalls: data?.activeCalls || [],
        successfulCalls: data?.successfulCalls || 0,
        totalCalls: data?.totalCalls || 0,
        winRate: data?.winRate || 0,
        xpHistory: data?.xpHistory || [],
        timestamp: new Date()
      };

      const userCallbacks = this.monitorCallbacks.get(userId);
      if (userCallbacks) {
        userCallbacks.forEach(cb => cb(xpData));
      }
    }, (error) => {
      if (!error.message?.includes('Missing or insufficient permissions')) {
        logger.error('XP monitoring error for user:', userId, error);
      }
      const userCallbacks = this.monitorCallbacks.get(userId);
      if (userCallbacks) {
        userCallbacks.forEach(cb => cb({
          error: true, message: error.message,
          xp: 0, activeCalls: [], successfulCalls: 0, totalCalls: 0,
          winRate: 0, xpHistory: [], timestamp: new Date()
        }));
      }
    });

    this.monitorListeners.set(userId, unsubscribe);

    return () => {
      const cbs = this.monitorCallbacks.get(userId);
      if (cbs) {
        cbs.delete(onUpdate);
        if (cbs.size === 0) this.stopMonitoring(userId);
      }
    };
  }

  stopMonitoring(userId) {
    const unsubscribe = this.monitorListeners.get(userId);
    if (unsubscribe) unsubscribe();
    this.monitorListeners.delete(userId);
    this.monitorCallbacks.delete(userId);
    this.lastKnownXP.delete(userId);
  }

  cleanup() {
    this.monitorListeners.forEach(unsub => unsub());
    this.monitorListeners.clear();
    this.monitorCallbacks.clear();
    this.lastKnownXP.clear();
    this.syncQueue.clear();
    this.syncProcessing = false;
  }

  async _processSync() {
    if (this.syncQueue.size === 0) {
      this.syncProcessing = false;
      return;
    }

    const updates = Array.from(this.syncQueue.entries());
    this.syncQueue.clear();

    for (const [userId, xp] of updates) {
      try {
        await this._syncUserPosts(userId, xp);
      } catch (e) {
        if (!e.message?.includes('Missing or insufficient permissions')) {
          logger.error(`Failed to sync posts for ${userId}:`, e);
        }
      }
    }

    this.syncProcessing = false;
    if (this.syncQueue.size > 0) {
      this.syncProcessing = true;
      setTimeout(() => this._processSync(), 500);
    }
  }

  async _syncUserPosts(userId, xp) {
    const postsQuery = query(
      collection(db, 'posts'),
      where('userId', '==', userId),
      where('type', '==', 'prediction')
    );

    const snapshot = await getDocs(postsQuery);
    const batch = [];
    snapshot.forEach(docSnap => {
      if (docSnap.data().userXP !== xp) {
        batch.push(updateDoc(docSnap.ref, { userXP: xp, lastUpdated: new Date() }));
      }
    });

    if (batch.length > 0) await Promise.all(batch);
  }

  // ===========================================================================
  // MARKET CAP CALCULATIONS (from xpCalculationService)
  // ===========================================================================

  getMarketCapTier(marketCap) {
    return this.marketCapTiers.find(tier => marketCap >= tier.min && marketCap < tier.max) || this.marketCapTiers[0];
  }

  getTargetTier(multiplier) {
    const percentGain = multiplier - 1;
    return this.targetMultipliers.find(tier => percentGain >= tier.min && percentGain < tier.max) || this.targetMultipliers[0];
  }

  getTimeMultiplier(hours) {
    return this.timeMultipliers.reduce((prev, curr) =>
      Math.abs(curr.hours - hours) < Math.abs(prev.hours - hours) ? curr : prev
    ).multiplier;
  }

  calculatePredictionXP(params) {
    const {
      marketCap = 100000,
      targetMultiplier = 1.5,
      timeHours = 24,
      bonusXP = 0
    } = params;

    const marketCapTier = this.getMarketCapTier(marketCap);
    const targetTier = this.getTargetTier(targetMultiplier);
    const timeMultiplier = this.getTimeMultiplier(timeHours);

    let baseXP = Math.floor(Math.floor(targetTier.xpBase * marketCapTier.multiplier) * timeMultiplier);
    const totalXP = baseXP + bonusXP;

    return {
      baseXP,
      bonusXP,
      totalXP,
      marketCapTier: marketCapTier.label,
      marketCapMultiplier: marketCapTier.multiplier,
      targetTier: targetTier.label,
      targetPercentage: ((targetMultiplier - 1) * 100).toFixed(1) + '%',
      timeMultiplier,
      riskRewardRatio: (totalXP / targetTier.xpBase).toFixed(2),
      breakdown: {
        base: targetTier.xpBase,
        afterMarketCap: Math.floor(targetTier.xpBase * marketCapTier.multiplier),
        afterTime: baseXP,
        withBonus: totalXP
      }
    };
  }

  getTargetOptions(marketCap) {
    const options = [];
    const calc = (mult) => this.calculatePredictionXP({ marketCap, targetMultiplier: mult }).totalXP;

    if (marketCap >= 1000000) {
      options.push(
        { multiplier: 1.05, label: '+5%', difficulty: 'Easy', xp: calc(1.05) },
        { multiplier: 1.10, label: '+10%', difficulty: 'Easy', xp: calc(1.10) },
        { multiplier: 1.25, label: '+25%', difficulty: 'Medium', xp: calc(1.25) },
        { multiplier: 1.50, label: '+50%', difficulty: 'Hard', xp: calc(1.50) },
        { multiplier: 2.00, label: '2x', difficulty: 'Very Hard', xp: calc(2.00) }
      );
    } else if (marketCap >= 100000) {
      options.push(
        { multiplier: 1.10, label: '+10%', difficulty: 'Easy', xp: calc(1.10) },
        { multiplier: 1.25, label: '+25%', difficulty: 'Easy', xp: calc(1.25) },
        { multiplier: 1.50, label: '+50%', difficulty: 'Medium', xp: calc(1.50) },
        { multiplier: 2.00, label: '2x', difficulty: 'Hard', xp: calc(2.00) },
        { multiplier: 5.00, label: '5x', difficulty: 'Very Hard', xp: calc(5.00) }
      );
    } else {
      options.push(
        { multiplier: 1.25, label: '+25%', difficulty: 'Easy', xp: calc(1.25) },
        { multiplier: 1.50, label: '+50%', difficulty: 'Easy', xp: calc(1.50) },
        { multiplier: 2.00, label: '2x', difficulty: 'Medium', xp: calc(2.00) },
        { multiplier: 5.00, label: '5x', difficulty: 'Hard', xp: calc(5.00) },
        { multiplier: 10.00, label: '10x', difficulty: 'Legendary', xp: calc(10.00) }
      );
    }

    return options;
  }

  validatePrediction(marketCap, currentPrice, targetMultiplier) {
    const errors = [];
    const warnings = [];

    if (targetMultiplier < 1.05) errors.push('Minimum target is 5% gain');
    if (marketCap >= 10000000 && targetMultiplier > 5) warnings.push('5x+ is extremely unlikely for tokens over $10M market cap');
    else if (marketCap >= 1000000 && targetMultiplier > 10) warnings.push('10x+ is very rare for tokens over $1M market cap');
    else if (targetMultiplier > 100) warnings.push('100x predictions are extremely rare');
    if (!marketCap || marketCap <= 0) warnings.push('Market cap unknown - XP calculations may be inaccurate');

    return { valid: errors.length === 0, errors, warnings };
  }

  // ===========================================================================
  // RECALCULATION (Admin / Fix XP)
  // ===========================================================================

  async recalculateAndCheck(userId) {
    try {
      const priceCallsQuery = query(
        collection(db, 'priceCalls'),
        where('userId', '==', userId)
      );

      const snapshot = await getDocs(priceCallsQuery);
      let totalXPGained = 0;
      let totalXPLost = 0;
      let predictionsFixed = 0;

      for (const predDoc of snapshot.docs) {
        const prediction = predDoc.data();

        if (prediction.status === 'won' || (prediction.status === 'completed' && prediction.outcome === 'won')) {
          totalXPGained += (prediction.xpRisk || 0) + (prediction.bonusXPWager || 0);
          continue;
        }

        if (prediction.status === 'lost' || (prediction.status === 'completed' && prediction.outcome === 'lost')) {
          totalXPLost += (prediction.xpRisk || 0);
          continue;
        }

        if (prediction.status === 'active' && !prediction.isResolved) {
          const shouldWin = await this._checkIfWon(prediction);

          if (shouldWin) {
            await updateDoc(predDoc.ref, {
              status: 'completed',
              isResolved: true,
              outcome: 'won',
              resolvedAt: serverTimestamp(),
              resolvedBy: 'xp_recalculation'
            });
            totalXPGained += (prediction.xpRisk || 0) + (prediction.bonusXPWager || 0);
            predictionsFixed++;
          }

          const expiresAt = this._parseDate(prediction.expiresAt);
          if (expiresAt && expiresAt < new Date()) {
            await updateDoc(predDoc.ref, {
              status: 'completed',
              isResolved: true,
              outcome: 'lost',
              resolvedAt: serverTimestamp(),
              resolvedBy: 'xp_recalculation'
            });
            totalXPLost += (prediction.xpRisk || 0);
            predictionsFixed++;
          }
        }
      }

      const userDoc = await getDoc(doc(db, 'users', userId));
      const currentStoredXP = userDoc.exists() ? (userDoc.data().xp || 0) : 0;
      const calculatedXP = Math.max(0, totalXPGained - totalXPLost);
      const finalXP = Math.max(currentStoredXP, calculatedXP);

      // Update both collections
      await updateDoc(doc(db, 'users', userId), { xp: finalXP, lastXPUpdate: serverTimestamp() });

      const rankingRef = doc(db, 'userRankings', userId);
      const rankingDoc = await getDoc(rankingRef);

      if (rankingDoc.exists()) {
        await updateDoc(rankingRef, {
          xp: finalXP,
          currentRank: this.getRankByXP(finalXP).id,
          updatedAt: serverTimestamp()
        });
      } else {
        await setDoc(rankingRef, {
          userId,
          xp: finalXP,
          currentRank: this.getRankByXP(finalXP).id,
          totalCalls: snapshot.size,
          successfulCalls: 0,
          activeCalls: [],
          createdAt: serverTimestamp()
        });
      }

      return {
        success: true, xp: finalXP, gained: totalXPGained,
        lost: totalXPLost, fixed: predictionsFixed, previousXP: currentStoredXP
      };
    } catch (error) {
      logger.error('XP Recalculation error:', error);
      return { success: false, error: error.message };
    }
  }

  async _checkIfWon(prediction) {
    try {
      const fullData = await priceService.getDexScreenerFullData(prediction.tokenMint);
      if (!fullData) return false;

      const currentMarketCap = fullData.marketCap;

      if (prediction.targetMarketCap && currentMarketCap >= prediction.targetMarketCap) return true;

      if (prediction.marketCap && (prediction.multiplier || prediction.targetMultiplier)) {
        const multiplier = prediction.multiplier || prediction.targetMultiplier;
        if (currentMarketCap >= prediction.marketCap * multiplier) return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  // ===========================================================================
  // TRADING POSTS (Legacy trade XP system)
  // ===========================================================================

  async submitTradingPost(userId, tradingData) {
    try {
      if (!userId || !tradingData) throw new Error('Missing required data: userId or tradingData');
      if (!tradingData.tokenSymbol || !tradingData.entryPrice) throw new Error('Missing required trading data: tokenSymbol and entryPrice');

      const entryPrice = parseFloat(tradingData.entryPrice);
      if (isNaN(entryPrice) || entryPrice <= 0) throw new Error('Invalid entry price format');

      if (tradingData.exitPrice) {
        const exitPrice = parseFloat(tradingData.exitPrice);
        if (isNaN(exitPrice) || exitPrice <= 0) throw new Error('Invalid exit price format');
      }

      const tradeId = `${userId}_${Date.now()}`;

      const trade = {
        id: tradeId,
        userId,
        ...tradingData,
        status: 'pending',
        submittedAt: Date.now(),
        xpAwarded: 0,
        verificationAttempts: 0,
        entryPrice: entryPrice.toString(),
        exitPrice: tradingData.exitPrice ? parseFloat(tradingData.exitPrice).toString() : '',
        positionSize: tradingData.positionSize ? parseFloat(tradingData.positionSize).toString() : '',
        pnl: Number(tradingData.pnl) || 0,
        pnlPercentage: Number(tradingData.pnlPercentage) || 0
      };

      this.pendingTrades.set(tradeId, trade);

      if (tradingData.exitPrice && tradingData.entryPrice) {
        this.verificationQueue.push(tradeId);
      }

      return {
        success: true,
        tradeId,
        message: tradingData.exitPrice ?
          'Trade submitted for verification and XP calculation' :
          'Entry call recorded. Submit exit for XP calculation.'
      };
    } catch (error) {
      logger.error('Error submitting trading post:', error);
      return { success: false, error: error.message };
    }
  }

  async awardXP(userId, xpResult) {
    try {
      const reason = `\uD83D\uDE80 ${xpResult.tradePerformance.tokenSymbol} pump call: ${xpResult.tradePerformance.pnlPercentage}% gain (${xpResult.tradePerformance.entryPrice} \u2192 ${xpResult.tradePerformance.exitPrice})`;
      await userService.awardXP(userId, xpResult.finalXP, reason, `trade_${Date.now()}`);
      return { success: true, xpAwarded: xpResult.finalXP, reason };
    } catch (error) {
      logger.error('Error awarding XP:', error);
      return { success: false, error: error.message };
    }
  }

  async manualVerification(tradeId, isVerified, adminUserId) {
    const trade = this.pendingTrades.get(tradeId);
    if (!trade) return { success: false, error: 'Trade not found' };

    try {
      if (isVerified) {
        const xpResult = await this._calculateTradeXP(trade, { verified: true, confidence: 1.0 });
        await this.awardXP(trade.userId, xpResult);
        trade.status = 'manually_verified';
        trade.xpAwarded = xpResult.finalXP;
        trade.verifiedBy = adminUserId;
      } else {
        trade.status = 'manually_rejected';
        trade.rejectedBy = adminUserId;
      }
      return { success: true, trade };
    } catch (error) {
      logger.error('Error in manual verification:', error);
      return { success: false, error: error.message };
    }
  }

  _startTradeProcessing() {
    this.processingInterval = setInterval(() => {
      this._processVerificationQueue();
    }, 30000);
  }

  async _processVerificationQueue() {
    if (this.verificationQueue.length === 0) return;

    const tradeId = this.verificationQueue.shift();
    const trade = this.pendingTrades.get(tradeId);
    if (!trade) return;

    try {
      const verification = await this._verifyTrade(trade);

      if (verification.verified) {
        const xpResult = await this._calculateTradeXP(trade, verification);
        await this.awardXP(trade.userId, xpResult);
        trade.status = 'verified';
        trade.xpAwarded = xpResult.finalXP;
      } else {
        trade.verificationAttempts++;
        if (trade.verificationAttempts >= 3) {
          trade.status = 'failed';
        } else {
          setTimeout(() => this.verificationQueue.push(tradeId), 60000);
        }
      }
    } catch (error) {
      logger.error('Error processing verification:', error);
      trade.status = 'error';
    }
  }

  async _verifyTrade(trade) {
    try {
      const currentPriceData = await priceService.getTokenPrice(trade.tokenAddress, trade.tokenSymbol);
      if (!currentPriceData) return { verified: false, reason: 'Could not fetch price data' };

      return {
        verified: true,
        priceData: currentPriceData,
        entryPriceActual: trade.entryPrice,
        exitPriceActual: trade.exitPrice,
        confidence: 0.8
      };
    } catch (error) {
      return { verified: false, reason: error.message };
    }
  }

  async _calculateTradeXP(trade, verification) {
    const priceMultiplier = parseFloat(trade.exitPrice) / parseFloat(trade.entryPrice);
    const volumeWeight = Math.min(3, Math.max(0.5, Math.log10(verification.priceData.volume24h / 1000) || 1));
    const accuracyFactor = verification.confidence || 1.0;
    const earlyDetectionBonus = priceMultiplier > 2 ? 1.5 : 1.0;

    const baseXP = calculateXP({
      priceMultiplier,
      volumeWeight,
      accuracyFactor,
      detectionTime: trade.timestamp,
      announcementTime: null
    });

    const finalXP = Math.round(baseXP * earlyDetectionBonus);

    return {
      baseXP,
      finalXP,
      factors: {
        priceMultiplier: priceMultiplier.toFixed(2),
        volumeWeight: volumeWeight.toFixed(2),
        accuracyFactor: accuracyFactor.toFixed(2),
        earlyDetectionBonus: earlyDetectionBonus.toFixed(2)
      },
      tradePerformance: {
        pnlPercentage: ((priceMultiplier - 1) * 100).toFixed(2),
        tokenSymbol: trade.tokenSymbol,
        entryPrice: trade.entryPrice,
        exitPrice: trade.exitPrice
      }
    };
  }

  // ===========================================================================
  // PREDICTION MONITORING (consolidated from predictionMonitorService)
  // ===========================================================================

  // Listen to user's active predictions in real time
  subscribeToUserPredictions(userId, callback) {
    const listenerKey = `predictions_${userId}`;
    if (this.monitorListeners.has(listenerKey)) {
      return this.monitorListeners.get(listenerKey);
    }

    const q = query(
      collection(db, 'priceCalls'),
      where('userId', '==', userId),
      where('status', '==', 'active'),
      where('isResolved', '==', false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const predictions = [];
      snapshot.forEach((docSnap) => {
        predictions.push({ id: docSnap.id, ...docSnap.data() });
      });
      predictions.sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate());
      callback(predictions);
    });

    this.monitorListeners.set(listenerKey, unsubscribe);
    return unsubscribe;
  }

  unsubscribeFromPredictions(userId) {
    const listenerKey = `predictions_${userId}`;
    const listener = this.monitorListeners.get(listenerKey);
    if (listener) {
      listener();
      this.monitorListeners.delete(listenerKey);
    }
  }

  // Check if a prediction hit its target (uses priceService)
  async checkPredictionTarget(prediction) {
    const currentPrice = await priceService.getDexScreenerPrice(prediction.tokenMint);
    if (!currentPrice) return { hit: false, currentPrice: null };

    const targetPrice = prediction.currentPrice * prediction.multiplier;
    return {
      hit: currentPrice >= targetPrice,
      currentPrice,
      targetPrice,
      progress: (currentPrice / targetPrice) * 100
    };
  }

  // Get prediction status with live price
  async getPredictionStatus(prediction) {
    const currentPrice = await priceService.getDexScreenerPrice(prediction.tokenMint);
    if (!currentPrice) {
      return { status: 'unknown', message: 'Price data unavailable' };
    }

    const targetPrice = prediction.currentPrice * prediction.multiplier;
    const progress = (currentPrice / targetPrice) * 100;

    // Check if expired
    if (prediction.expiresAt && prediction.expiresAt.toDate() < new Date()) {
      return { status: 'expired', message: 'Prediction expired', currentPrice, targetPrice, progress };
    }

    // Check if won
    if (currentPrice >= targetPrice) {
      return { status: 'won', message: 'Target hit! Victory!', currentPrice, targetPrice, progress: 100 };
    }

    // Still active
    const timeLeft = prediction.expiresAt
      ? Math.max(0, prediction.expiresAt.toDate() - new Date())
      : null;

    return {
      status: 'active',
      message: `${progress.toFixed(1)}% to target`,
      currentPrice, targetPrice, progress, timeLeft
    };
  }

  // Request manual check via cloud function trigger
  async manualCheckPrediction(predictionId) {
    try {
      const predictionRef = doc(db, 'priceCalls', predictionId);
      await updateDoc(predictionRef, {
        lastManualCheck: serverTimestamp(),
        checkRequested: true
      });
      return { success: true };
    } catch (error) {
      logger.error('Error requesting manual check:', error);
      return { success: false, error: error.message };
    }
  }

  // ===========================================================================
  // PREDICTION POST CHECKING (consolidated from simpleXPChecker)
  // ===========================================================================

  // Check a prediction post and award/deduct XP if needed
  async checkPredictionPost(postId, userId) {
    // Prevent concurrent checks on same post
    const cacheKey = `checking_${postId}`;
    if (this.priceCallCache.has(cacheKey)) {
      return this.priceCallCache.get(cacheKey);
    }

    const checkPromise = this._performPostCheck(postId, userId);
    this.priceCallCache.set(cacheKey, checkPromise);

    try {
      const result = await checkPromise;
      return result;
    } finally {
      this.priceCallCache.delete(cacheKey);
    }
  }

  async _performPostCheck(postId, userId) {
    try {
      // Check xpAwards to prevent double-awarding
      const awardRef = doc(db, 'xpAwards', postId);
      const awardDoc = await getDoc(awardRef);
      if (awardDoc.exists()) {
        return { alreadyAwarded: true, awardData: awardDoc.data() };
      }

      // Get the post data
      const postRef = doc(db, 'posts', postId);
      const postDoc = await getDoc(postRef);
      if (!postDoc.exists()) return { error: 'Post not found' };

      const post = postDoc.data();
      if (post.type !== 'prediction' || !post.predictionData) return { error: 'Not a prediction post' };

      const prediction = post.predictionData;
      if (prediction.isResolved || prediction.status !== 'active') return { error: 'Prediction already resolved' };

      // Check if expired
      const now = Date.now();
      const expiresAt = prediction.expiresAt?.toMillis ? prediction.expiresAt.toMillis() : prediction.expiresAt;
      if (now >= expiresAt) {
        await this._handleExpiredPost(postId, userId, prediction);
        return { expired: true, xpLost: prediction.xpRisk || 0 };
      }

      // Get current price from priceService
      const fullData = await priceService.getDexScreenerFullData(prediction.tokenMint);
      if (!fullData) return { error: 'Could not fetch current price' };

      const currentPrice = fullData.price;
      const currentMarketCap = fullData.marketCap;

      // Check if target reached
      let targetReached = false;
      let victoryReason = '';

      const priceMultiplier = currentPrice / prediction.currentPrice;
      if (priceMultiplier >= prediction.multiplier) {
        targetReached = true;
        victoryReason = `Price ${priceMultiplier.toFixed(2)}x >= ${prediction.multiplier}x`;
      }

      if (prediction.targetMarketCap && currentMarketCap >= prediction.targetMarketCap) {
        targetReached = true;
        victoryReason = `Market cap $${(currentMarketCap/1000000).toFixed(2)}M >= $${(prediction.targetMarketCap/1000000).toFixed(2)}M`;
      }

      if (!targetReached) {
        const progress = Math.min(100, (priceMultiplier / prediction.multiplier) * 100);
        return { notReached: true, progress: progress.toFixed(1), currentPrice, currentMarketCap };
      }

      // VICTORY! Award XP
      logger.log(`Victory for post ${postId}! ${victoryReason}`);
      const xpReward = prediction.potentialReward || 0;

      // Record award
      await setDoc(awardRef, {
        postId, userId, xpAwarded: xpReward, awardedAt: serverTimestamp(),
        reason: victoryReason, tokenSymbol: prediction.tokenSymbol,
        currentPrice, currentMarketCap,
        targetPrice: prediction.targetPrice, targetMarketCap: prediction.targetMarketCap
      });

      // Award XP via existing completePrediction
      await this.completePrediction(userId, postId, true, xpReward);

      // Update the post
      await updateDoc(postRef, {
        'predictionData.status': 'won',
        'predictionData.isResolved': true,
        'predictionData.xpAwarded': xpReward,
        'predictionData.actualPrice': currentPrice,
        'predictionData.actualMarketCap': currentMarketCap,
        'predictionData.resolvedAt': serverTimestamp()
      });

      return { success: true, xpAwarded: xpReward, reason: victoryReason };
    } catch (error) {
      logger.error(`Error checking post ${postId}:`, error);
      return { error: error.message };
    }
  }

  async _handleExpiredPost(postId, userId, prediction) {
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      'predictionData.status': 'lost',
      'predictionData.isResolved': true,
      'predictionData.xpAwarded': 0,
      'predictionData.resolvedAt': serverTimestamp()
    });

    // Deduct XP via existing completePrediction
    const xpLoss = prediction.xpRisk || 0;
    if (xpLoss > 0) {
      await this.completePrediction(userId, postId, false);
    }
  }

  // Batch check multiple prediction posts
  async checkMultiplePredictionPosts(posts) {
    const results = [];
    for (const post of posts) {
      if (post.type === 'prediction' && post.predictionData?.status === 'active') {
        const result = await this.checkPredictionPost(post.id, post.userId);
        results.push({ postId: post.id, ...result });
      }
    }
    return results;
  }

  // ===========================================================================
  // UTILITIES
  // ===========================================================================

  // Delegates to priceService (kept for backward compatibility)
  async getSOLPrice() {
    return priceService.getSOLPrice();
  }

  async getCurrentTokenPrice(tokenMint) {
    return priceService.getDexScreenerPrice(tokenMint);
  }

  async createNotification(userId, type, data = {}) {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId,
        type,
        timestamp: serverTimestamp(),
        read: false,
        ...data
      });
    } catch (error) {
      logger.error(`Error creating ${type} notification:`, error);
    }
  }

  _parseDate(dateValue) {
    if (!dateValue) return null;
    if (typeof dateValue.toDate === 'function') return dateValue.toDate();
    if (dateValue instanceof Date) return dateValue;
    if (typeof dateValue === 'number') return new Date(dateValue);
    try { return new Date(dateValue); } catch { return null; }
  }
}

const xpService = new XPService();
export default xpService;
